import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { HeadlineM, LabelS } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { glossary } from '#src/lib/glossary.ts';

import type { PrivacyPolicyDialog_dataPartner } from './__generated__/index.graphql.ts';
import { useUpdatePartnerOffersAgreement } from './useUpdatePartnerOffersAgreement/index.ts';

const DialogContainer = styled(Vertical).attrs({ gap: 2, center: true })`
  text-align: center;
  padding: var(--unit) var(--triple-unit) var(--triple-unit) var(--triple-unit);
`;
const FlexContainer = styled.div`
  width: 100%;
  display: flex;
  gap: var(--unit);
`;

type Props = {
  onClose: () => void;
  dataPartner: PrivacyPolicyDialog_dataPartner;
};
export const PrivacyPolicyDialog = ({ onClose, dataPartner }: Props) => {
  const { slug, name, termsUrl } = dataPartner;
  const updateAgreement = useUpdatePartnerOffersAgreement();

  const onYes = () => {
    updateAgreement({ partner: slug, value: true });
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title={
        <HeadlineM as="h4" className="text-center">
          <FormattedMessage defaultMessage="Trusted partners" />
        </HeadlineM>
      }
    >
      <DialogContainer>
        <FormattedMessage
          defaultMessage="Would you like to share your information with {clubName} for marketing and promotional purposes?"
          values={{ clubName: name }}
        />
        <FlexContainer>
          <Button size="small" fullWidth color="red" onClick={onClose}>
            <FormattedMessage {...glossary.no} />
          </Button>
          <Button size="small" fullWidth color="primary" onClick={onYes}>
            <FormattedMessage {...glossary.yes} />
          </Button>
        </FlexContainer>
        <LabelS color="var(--c-nd-400)">
          <FormattedMessage
            defaultMessage="Please note that you will be able to withdraw your consent to the processing of your data by {clubName} at any time. In order to do so, please refer to {clubName} privacy policy <a>here</a>."
            values={{
              clubName: name,
              termsUrl,
              a: chunks => (
                <a href={termsUrl} target="_blank" rel="noreferrer">
                  {chunks}
                </a>
              ),
            }}
          />
        </LabelS>
      </DialogContainer>
    </Dialog>
  );
};

PrivacyPolicyDialog.fragments = {
  dataPartner: gql`
    fragment PrivacyPolicyDialog_dataPartner on DataPartner {
      slug
      name
      termsUrl
    }
  ` as TypedDocumentNode<PrivacyPolicyDialog_dataPartner>,
};
